import { useMemo } from 'react'

export const DOTS = 'dots-placeholder'

const range = (start: number, end: number) => {
  let length = end - start + 1
  return Array.from({ length }, (_, idx) => idx + start)
}

interface PaginationSelectorArgs {
  totalCount: number
  pageSize: number
  siblingCount?: number
  currentPage: number
  simplified?: boolean
}

/*
    PaginationSelectorArgs is an interface that contains the following properties:
    - totalCount: total number of items
    - pageSize: number of items per page
    - siblingCount: number of pages to show on each side of the current page
    - currentPage: current page number
    - simplified: whether to use the simplified pagination or not (Only show the last three pages)
    Reference: https://www.freecodecamp.org/news/build-a-custom-pagination-component-in-react/
*/
export const usePaginationSelector = ({
  totalCount,
  pageSize,
  siblingCount = 1,
  currentPage,
  simplified = false,
}: PaginationSelectorArgs) => {
  return useMemo(() => {
    const totalPageCount = Math.ceil(totalCount / pageSize)

    if (simplified) {
      // If current page is one of the last two pages, simply show the last three pages
      if (currentPage >= totalPageCount - 1) {
        return range(Math.max(totalPageCount - 2, 1), totalPageCount)
      }

      return range(currentPage, Math.min(currentPage + 2, totalPageCount))
    }

    // Pages count is determined as siblingCount + firstPage + lastPage + currentPage + 2*DOTS
    const totalPageNumbers = siblingCount + 5

    if (totalPageNumbers >= totalPageCount) {
      return range(1, totalPageCount)
    }

    const leftSiblingIndex = Math.max(currentPage - siblingCount, 1)
    const rightSiblingIndex = Math.min(
      currentPage + siblingCount,
      totalPageCount,
    )

    const shouldShowLeftDots = leftSiblingIndex > 2
    const shouldShowRightDots = rightSiblingIndex < totalPageCount - 2

    const firstPageIndex = 1
    const lastPageIndex = totalPageCount

    if (!shouldShowLeftDots && shouldShowRightDots) {
      let leftItemCount = 3 + 2 * siblingCount
      let leftRange = range(1, leftItemCount)

      return [...leftRange, DOTS, totalPageCount]
    }

    if (shouldShowLeftDots && !shouldShowRightDots) {
      let rightItemCount = 3 + 2 * siblingCount
      let rightRange = range(
        totalPageCount - rightItemCount + 1,
        totalPageCount,
      )
      return [firstPageIndex, DOTS, ...rightRange]
    }

    if (shouldShowLeftDots && shouldShowRightDots) {
      let middleRange = range(leftSiblingIndex, rightSiblingIndex)
      return [firstPageIndex, DOTS, ...middleRange, DOTS, lastPageIndex]
    }
  }, [totalCount, pageSize, siblingCount, currentPage])
}
